
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  Code, 
  Play, 
  CheckCircle, 
  XCircle,
  TrendingUp,
  Users,
  Clock,
  Target
} from 'lucide-react';

const PythonAnalyticsDashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, [timeRange]);

  // Mock Python learning data
  const pythonProgressData = [
    { date: '2024-01-01', basics: 45, oop: 23, advanced: 12 },
    { date: '2024-01-02', basics: 52, oop: 31, advanced: 18 },
    { date: '2024-01-03', basics: 48, oop: 28, advanced: 22 },
    { date: '2024-01-04', basics: 61, oop: 35, advanced: 28 },
    { date: '2024-01-05', basics: 58, oop: 42, advanced: 31 },
    { date: '2024-01-06', basics: 67, oop: 48, advanced: 35 },
    { date: '2024-01-07', basics: 72, oop: 52, advanced: 41 }
  ];

  const codeExecutionData = [
    { concept: 'Variables', success: 85, failed: 15 },
    { concept: 'Functions', success: 78, failed: 22 },
    { concept: 'Classes', success: 65, failed: 35 },
    { concept: 'Loops', success: 82, failed: 18 },
    { concept: 'Data Structures', success: 71, failed: 29 }
  ];

  const skillDistribution = [
    { name: 'Beginner', value: 45, color: '#10b981' },
    { name: 'Intermediate', value: 35, color: '#3b82f6' },
    { name: 'Advanced', value: 20, color: '#8b5cf6' }
  ];

  const pythonStats = {
    totalSessions: 1247,
    codeExecutions: 3892,
    avgSessionTime: 28,
    successRate: 76.3
  };

  const timeRanges = [
    { id: '7d', label: '7 Days' },
    { id: '30d', label: '30 Days' },
    { id: '90d', label: '90 Days' }
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded mb-2"></div>
                <div className="h-8 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Code className="h-6 w-6 text-blue-600" />
            Python Learning Analytics
          </h2>
          <p className="text-muted-foreground">Comprehensive Python learning progress and code execution insights</p>
        </div>
        <div className="flex gap-2">
          {timeRanges.map((range) => (
            <Button
              key={range.id}
              variant={timeRange === range.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTimeRange(range.id as any)}
            >
              {range.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Python Learning Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Sessions</p>
                <p className="text-2xl font-bold">{pythonStats.totalSessions}</p>
                <p className="text-xs text-green-600">+15% from last month</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Code Executions</p>
                <p className="text-2xl font-bold">{pythonStats.codeExecutions}</p>
                <p className="text-xs text-green-600">+23% from last month</p>
              </div>
              <Play className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg. Session Time</p>
                <p className="text-2xl font-bold">{pythonStats.avgSessionTime}m</p>
                <p className="text-xs text-green-600">+8% from last month</p>
              </div>
              <Clock className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                <p className="text-2xl font-bold">{pythonStats.successRate}%</p>
                <p className="text-xs text-green-600">+5.2% from last month</p>
              </div>
              <Target className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Python Learning Progress */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Python Learning Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={pythonProgressData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="basics" stroke="#10b981" strokeWidth={2} />
                <Line type="monotone" dataKey="oop" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="advanced" stroke="#8b5cf6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Code Execution Success Rates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Code Execution Success by Concept
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={codeExecutionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="concept" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="success" fill="#10b981" />
                <Bar dataKey="failed" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Skill Level Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Skill Level Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={skillDistribution}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {skillDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Recent Python Activities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Recent Python Activities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">List Comprehension Exercise</p>
                  <p className="text-xs text-muted-foreground">Completed successfully - 2 minutes ago</p>
                </div>
                <Badge variant="outline" className="text-green-600">Success</Badge>
              </div>
              
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <XCircle className="h-4 w-4 text-red-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Class Inheritance Challenge</p>
                  <p className="text-xs text-muted-foreground">Failed attempt - 5 minutes ago</p>
                </div>
                <Badge variant="outline" className="text-red-600">Failed</Badge>
              </div>
              
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Function Parameters Quiz</p>
                  <p className="text-xs text-muted-foreground">Completed successfully - 8 minutes ago</p>
                </div>
                <Badge variant="outline" className="text-green-600">Success</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PythonAnalyticsDashboard;
